/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/users/CreateUserModal.tsx
 * PURPOSE: Modal form for creating new users - Tenant Admin feature
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance) + P6 (Component Library)
 * SPRINT GOVERNANCE: Rule 4: Tier-3 Code Review
 *
 * DATE: 2026-01-15
 * COMPLIANCE: SDLC 6.3.1 + NQH-BRAND-DESIGN-SYSTEM.md
 *
 * @business-context
 * - Domain: Vietnamese F&B Platform, User Management, Tenant Admin
 * - Impact: User Onboarding, Access Control, Team Management
 * - Stakeholders: Tenant Admins, Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Frontend Component Layer
 * - Pattern: Modal Form, Controlled Components, API Integration
 * - Dependencies: React, userApi, Design System Components
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - ADR: docs/02-design/18-adrs/ADR-frontend-auth-state-management.md
 * - API: POST /api/v1/users/
 * - Backend: backend/app/api/v1/users.py
 * - GAP Report: docs/08-collaborate/02-Progress-Reports/Pilot-Onboarding/USER-CREATION-GAP-REPORT.md
 */

import React, { useState } from 'react';
import { XMarkIcon, UserPlusIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { userApi } from '../../services/api/user';
import { useAuthStore } from '../../store/auth';
import {
  TenantUserCreate,
  TenantUserRole,
  CREATABLE_TENANT_ROLES,
  roleRequiresAnchor,
  UserResponse,
} from '../../types/user';
import { AnchorPicker } from '../tenant-admin/AnchorPicker';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: UserResponse) => void;
}

interface FormData {
  username: string;
  email: string;
  full_name: string;
  phone_number: string;
  password: string;
  confirmPassword: string;
  role: TenantUserRole;
  organization_id: string;
  position: string;
}

interface FormErrors {
  username?: string;
  email?: string;
  full_name?: string;
  phone_number?: string;
  password?: string;
  confirmPassword?: string;
  role?: string;
  organization_id?: string;
  general?: string;
}

const CREATABLE_ROLE_VALUES = CREATABLE_TENANT_ROLES.map((r) => r.value);

const INITIAL_FORM_DATA: FormData = {
  username: '',
  email: '',
  full_name: '',
  phone_number: '',
  password: '',
  confirmPassword: '',
  role: 'EMPLOYEE',
  organization_id: '',
  position: '',
};

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  // Per ADR-frontend-auth-state-management: use accessToken with alias
  const { accessToken: token } = useAuthStore();
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Username validation (SPEC-0208 §2.3: required by canonical TenantUserCreate)
    // Matches backend pattern ^[a-zA-Z0-9_-]+$, length 3-100
    if (!formData.username.trim()) {
      newErrors.username = 'Tên đăng nhập là bắt buộc';
    } else if (!/^[a-zA-Z0-9_-]{3,100}$/.test(formData.username.trim())) {
      newErrors.username =
        'Tên đăng nhập 3-100 ký tự, chỉ gồm chữ, số, gạch dưới hoặc gạch ngang';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email là bắt buộc';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email không hợp lệ';
    }

    // Full name validation
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Họ và tên là bắt buộc';
    } else if (formData.full_name.trim().length < 2) {
      newErrors.full_name = 'Họ và tên phải có ít nhất 2 ký tự';
    }

    // Phone number validation (optional but must be valid if provided)
    if (formData.phone_number && !/^[0-9+()-]{10,20}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Số điện thoại không hợp lệ (10-20 ký tự)';
    }

    // Password validation (per backend: 8+ chars, 1 digit, 1 uppercase)
    if (!formData.password) {
      newErrors.password = 'Mật khẩu là bắt buộc';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Mật khẩu phải có ít nhất 8 ký tự';
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Mật khẩu phải chứa ít nhất 1 chữ số';
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Mật khẩu phải chứa ít nhất 1 chữ hoa';
    }

    // Confirm password
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';
    }

    // Role validation — must be one of the 13 canonical creatable TenantUserRole values
    if (!CREATABLE_ROLE_VALUES.includes(formData.role)) {
      newErrors.role = 'Vai trò không hợp lệ';
    }

    // Org-anchor validation (SPEC-0208 §3.3 / chk_tenant_users_anchor_required):
    // required for all roles except TENANT_ADMIN. Block client-side to avoid a
    // 422 E_ANCHOR_REQUIRED round-trip.
    if (roleRequiresAnchor(formData.role) && !formData.organization_id) {
      newErrors.organization_id = 'Vui lòng chọn đơn vị (anchor) cho vai trò này.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setErrors({ general: 'Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại.' });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // SPEC-0208 §2.3/§3.2: canonical POST /api/v1/tenant-users/create expects
      // TenantUserCreate. Role is bound DIRECTLY to TenantUserRole (no mapping
      // table) and sent verbatim. organization_id is the scope anchor (DD-0056
      // §6.6); branch_id is deprecated (SPEC-0208 §3.3) and intentionally unused.
      const createData: TenantUserCreate = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        full_name: formData.full_name.trim(),
        phone_number: formData.phone_number.trim() || undefined,
        password: formData.password,
        role: formData.role,
        organization_id: formData.organization_id || undefined,
        position: formData.position.trim() || undefined,
      };

      const newUser = await userApi.createUser(token, createData);

      // Reset form and close modal
      setFormData(INITIAL_FORM_DATA);
      onSuccess(newUser);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error
        ? err.message
        : 'Không thể tạo người dùng. Vui lòng thử lại.';
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM_DATA);
    setErrors({});
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
                <UserPlusIcon className="h-5 w-5 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Thêm nhân viên mới
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4">
            {/* General Error */}
            {errors.general && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <span>{errors.general}</span>
              </div>
            )}

            {/* Username (SPEC-0208 §2.3: required by canonical tenant-users/create) */}
            <div className="mb-4">
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Tên đăng nhập <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.username
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="nguyenvana"
                disabled={isSubmitting}
              />
              {errors.username && (
                <p className="mt-1 text-xs text-red-600">{errors.username}</p>
              )}
            </div>

            {/* Email */}
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.email
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="email@example.com"
                disabled={isSubmitting}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
                Họ và tên <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.full_name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="Nguyễn Văn A"
                disabled={isSubmitting}
              />
              {errors.full_name && (
                <p className="mt-1 text-xs text-red-600">{errors.full_name}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="mb-4">
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-1">
                Số điện thoại
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.phone_number
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="0901234567"
                disabled={isSubmitting}
              />
              {errors.phone_number && (
                <p className="mt-1 text-xs text-red-600">{errors.phone_number}</p>
              )}
            </div>

            {/* Role */}
            <div className="mb-4">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                Vai trò <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.role
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                disabled={isSubmitting}
              >
                {CREATABLE_TENANT_ROLES.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-1 text-xs text-red-600">{errors.role}</p>
              )}
            </div>

            {/* Organization anchor (SPEC-0208 §3.3 / DD-0051): required for all
                roles except TENANT_ADMIN. Reuses the existing AnchorPicker, which
                is backed by useOrganizationTree. Sends organization_id (UUID);
                branch_id is deprecated and not used. */}
            <div className="mb-4">
              <AnchorPicker
                value={formData.organization_id}
                onChange={(organizationId) => {
                  setFormData((prev) => ({ ...prev, organization_id: organizationId }));
                  if (errors.organization_id) {
                    setErrors((prev) => ({ ...prev, organization_id: undefined }));
                  }
                }}
                required={roleRequiresAnchor(formData.role)}
                disabled={isSubmitting}
                label="Đơn vị (anchor)"
              />
              {errors.organization_id && (
                <p className="mt-1 text-xs text-red-600">{errors.organization_id}</p>
              )}
            </div>

            {/* Position */}
            <div className="mb-4">
              <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-1">
                Chức vụ
              </label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                placeholder="Nhân viên phục vụ"
                disabled={isSubmitting}
              />
            </div>

            {/* Password */}
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Mật khẩu <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.password
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="Tối thiểu 8 ký tự, 1 số, 1 chữ hoa"
                disabled={isSubmitting}
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Xác nhận mật khẩu <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                  errors.confirmPassword
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                }`}
                placeholder="Nhập lại mật khẩu"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200"
                disabled={isSubmitting}
              >
                Hủy
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Đang tạo...
                  </span>
                ) : (
                  'Tạo nhân viên'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal;